// src/app/pages/events/events.component.ts

import {
  Component,
  AfterViewInit,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';

import { EmailJsService } from '../../services/emailjs.service';

import { SeoService } from '../../services/seo.service';

import {
  CampaignEvent,
  CAMPAIGN_EVENTS
} from './events.data';

export type { CampaignEvent };

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule
  ],
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.css'],
})
export class EventsComponent implements OnInit, AfterViewInit {

  showPastEvents = false;

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly eventsCollectionId =
    `${this.baseUrl}/events#collection`;

  // ─── Invite form ────────────────────────────────────────────────────────────

  invite = {
    name: '',
    email: '',
    organization: '',
    eventName: '',
    eventDate: '',
    eventTime: '',
    eventLocation: '',
    attendance: '',
    details: ''
  };

  inviteSubmitting = false;

  inviteSubmitted = false;

  inviteError = '';

  constructor(
    private emailJs: EmailJsService,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    this.seo.update(
      'Events',
      'Join Lydia Christensen at community events across Michigan. Town halls, canvassing events, and signature drives.',
      'events'
    );

    this.setStructuredData();
  }

  // ─── Event getters ──────────────────────────────────────────────────────────

  private get now(): Date {
    return new Date();
  }

  get upcomingEvents(): CampaignEvent[] {
    return CAMPAIGN_EVENTS
      .filter(
        event =>
          event.recurring ||
          new Date(event.dateTime) >= this.now
      )
      .sort(
        (a, b) =>
          new Date(a.dateTime).getTime() -
          new Date(b.dateTime).getTime()
      );
  }

  get pastEvents(): CampaignEvent[] {
    return CAMPAIGN_EVENTS
      .filter(
        event =>
          !event.recurring &&
          new Date(event.dateTime) < this.now
      )
      .sort(
        (a, b) =>
          new Date(b.dateTime).getTime() -
          new Date(a.dateTime).getTime()
      );
  }

  // ─── Structured data ────────────────────────────────────────────────────────

  private setStructuredData(): void {
    const eventEntities = CAMPAIGN_EVENTS.map(
      (event, index) =>
        this.buildEventEntity(event, index)
    );

    this.seo.setStructuredData({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": this.eventsCollectionId,
          "url": `${this.baseUrl}/events`,
          "name": "Lydia Christensen Events",
          "description":
            "Community events, town halls, canvassing events, signature drives, and other events involving Lydia Christensen and her campaign.",
          "isPartOf": {
            "@id": this.seo.websiteId
          },
          "about": {
            "@id": this.seo.lydiaId
          },
          "publisher": {
            "@id": this.seo.campaignId
          },
          "hasPart": eventEntities.map(
            event => ({
              "@id": event["@id"]
            })
          )
        },
        ...eventEntities
      ]
    });
  }

  private buildEventEntity(
    event: CampaignEvent,
    index: number
  ): any {

    const slug = this.createEventSlug(
      event,
      index
    );

    const eventUrl =
      `${this.baseUrl}/events#${slug}`;

    const eventId =
      `${eventUrl}-event`;

    /*
     * Recurring event
     *
     * Your data identifies this as a weekly Sunday
     * meeting. Schema.org provides eventSchedule for
     * recurring events rather than requiring us to
     * manufacture a single startDate.
     */

    if (event.recurring) {

      return {
        "@type": "Event",
        "@id": eventId,
        "url": eventUrl,
        "name": event.title,
        "description": event.description,

        "eventSchedule": {
          "@type": "Schedule",
          "repeatFrequency": "P1W",
          "byDay": [
            "https://schema.org/Sunday"
          ],
          "startTime": this.extractStartTime(
            event.dateTime
          ),
          "scheduleTimezone": "America/Detroit"
        },

        "location":
          this.buildLocation(event),

        "organizer": {
          "@id": this.seo.campaignId
        },

        "performer": {
          "@id": this.seo.lydiaId
        },

        "about": {
          "@id": this.seo.lydiaId
        }
      };
    }

    /*
     * Normal one-time event.
     */

    const entity: any = {
      "@type": "Event",
      "@id": eventId,
      "url": eventUrl,
      "name": event.title,
      "description": event.description,
      "startDate": event.dateTime,

      "eventStatus":
        "https://schema.org/EventScheduled",

      "location":
        this.buildLocation(event),

      "organizer": {
        "@id": this.seo.campaignId
      },

      "performer": {
        "@id": this.seo.lydiaId
      },

      "about": {
        "@id": this.seo.lydiaId
      }
    };

    /*
     * If the event has a known RSVP URL, preserve it
     * as the event's RSVP/action URL.
     */

    if (event.rsvpUrl) {
      entity["offers"] = {
        "@type": "Offer",
        "url": this.resolveUrl(event.rsvpUrl)
      };
    }

    /*
     * Optional partner/host information.
     */

    if (event.partnerName) {
      entity["sponsor"] = {
        "@type": "Organization",
        "name": event.partnerName,
        ...(event.partnerUrl
          ? {
              "url": this.resolveUrl(
                event.partnerUrl
              )
            }
          : {})
      };
    }

    return entity;
  }

  private buildLocation(
    event: CampaignEvent
  ): any {

    /*
     * Virtual events use VirtualLocation.
     */

    if (event.virtual) {
      return {
        "@type": "VirtualLocation",
        "name": event.location
      };
    }

    /*
     * Physical events use Place.
     *
     * We only add address information that actually
     * exists in your events.data.ts.
     */

    const location: any = {
      "@type": "Place",
      "name": event.location
    };

    if (event.city) {

      const address: any = {
        "@type": "PostalAddress",
        "addressLocality": this.getCityName(
          event.city
        ),
        "addressRegion": "MI"
      };

      const postalCode =
        this.getPostalCode(event.city);

      if (postalCode) {
        address["postalCode"] = postalCode;
      }

      const streetAddress =
        this.getStreetAddress(event.location);

      if (streetAddress) {
        address["streetAddress"] =
          streetAddress;
      }

      location["address"] = address;
    }

    return location;
  }

  // ─── Event data helpers ─────────────────────────────────────────────────────

  private createEventSlug(
    event: CampaignEvent,
    index: number
  ): string {

    const titleSlug = event.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `${titleSlug}-${index + 1}`;
  }

  private extractStartTime(
    dateTime: string
  ): string {

    const timePart =
      dateTime.split('T')[1];

    if (!timePart) {
      return '';
    }

    return timePart.length === 5
      ? `${timePart}:00`
      : timePart;
  }

  private getCityName(
    city: string
  ): string {

    return city
      .replace(/,\s*MI\b.*$/i, '')
      .trim();
  }

  private getPostalCode(
    city: string
  ): string | null {

    const match =
      city.match(/\b\d{5}(?:-\d{4})?\b/);

    return match
      ? match[0]
      : null;
  }

  private getStreetAddress(
    location: string
  ): string | null {

    /*
     * Only treat the location as a street address
     * when it actually begins with a street number.
     *
     * Example:
     * "127 W Aurora St" → included
     * "Depot Park" → not fabricated into an address
     */

    const match =
      location.match(
        /^\d+\s+.+$/i
      );

    return match
      ? match[0].trim()
      : null;
  }

  private resolveUrl(
    url: string
  ): string {

    if (url.startsWith('http')) {
      return url;
    }

    if (url.startsWith('/')) {
      return `${this.baseUrl}${url}`;
    }

    return `${this.baseUrl}/${url}`;
  }

  // ─── Google Calendar ────────────────────────────────────────────────────────

  getGoogleCalendarUrl(
    event: CampaignEvent
  ): string {

    const [datePart, timePart = '00:00'] =
      event.dateTime.split('T');

    const [hours, minutes] =
      timePart.split(':').map(Number);

    /*
     * Build the date using the user's local calendar
     * fields instead of parsing the string as UTC.
     *
     * This prevents the calendar event from shifting
     * by an hour because of toISOString().
     */

    const start = new Date(
      Number(datePart.substring(0, 4)),
      Number(datePart.substring(5, 7)) - 1,
      Number(datePart.substring(8, 10)),
      hours,
      minutes
    );

    // Default event duration: 1 hour
    const end = new Date(
      start.getTime() +
      60 * 60 * 1000
    );

    const formatGoogleDate = (
      date: Date
    ): string => {

      const year =
        date.getFullYear();

      const month =
        String(date.getMonth() + 1)
          .padStart(2, '0');

      const day =
        String(date.getDate())
          .padStart(2, '0');

      const hours =
        String(date.getHours())
          .padStart(2, '0');

      const minutes =
        String(date.getMinutes())
          .padStart(2, '0');

      const seconds =
        String(date.getSeconds())
          .padStart(2, '0');

      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const details = [
      event.description,

      event.virtual && event.rsvpUrl
        ? `Join: ${event.rsvpUrl}`
        : ''
    ]
      .filter(Boolean)
      .join('\n\n');

    const location = event.virtual
      ? 'Online / Zoom'
      : `${event.location}${event.city ? ', ' + event.city : ''}`;

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates:
        `${formatGoogleDate(start)}/${formatGoogleDate(end)}`,
      details,
      location,
      ctz: 'America/Detroit'
    });

    return (
      `https://calendar.google.com/calendar/render?` +
      params.toString()
    );
  }

  // ─── Invite form handlers ───────────────────────────────────────────────────

  onInviteSubmit(): void {

    this.inviteSubmitting = true;

    this.inviteError = '';

    this.emailJs
      .sendEventInvite(this.invite)
      .then(() => {

        this.inviteSubmitted = true;

        this.inviteSubmitting = false;

      })
      .catch(() => {

        this.inviteError =
          'Something went wrong. Please try again or email us directly.';

        this.inviteSubmitting = false;

      });
  }

  resetInviteForm(): void {

    this.invite = {
      name: '',
      email: '',
      organization: '',
      eventName: '',
      eventDate: '',
      eventTime: '',
      eventLocation: '',
      attendance: '',
      details: ''
    };

    this.inviteSubmitted = false;

    this.inviteError = '';
  }

  // ─── Mobilize.us widget ─────────────────────────────────────────────────────

  private mobilizeOrgId: string | null = null;

  ngAfterViewInit(): void {

    if (this.mobilizeOrgId) {

      this.loadMobilizeWidget(
        this.mobilizeOrgId
      );
    }
  }

  private loadMobilizeWidget(
    orgId: string
  ): void {

    const existing =
      document.getElementById(
        'mobilize-script'
      );

    if (existing) {
      return;
    }

    const container =
      document.getElementById(
        'mobilize-widget'
      );

    if (!container) {
      return;
    }

    container.setAttribute(
      'data-organization-id',
      orgId
    );

    container.setAttribute(
      'data-width',
      '100%'
    );

    const script =
      document.createElement('script');

    script.id =
      'mobilize-script';

    script.src =
      'https://mobilize.us/widget/v2/widget.js';

    script.async = true;

    document.body.appendChild(
      script
    );
  }

}